<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import QuestionCard from "./QuestionCard.vue";

// The genie's questions, all open at once so answering is one tap each. Questions about
// what the user added on the Context tab come first.
const QUESTION_COUNT = 8;

const store = useStore();

const prompt = computed(() => store.state.prompt);
const questionList = computed(() => store.state.questions.questionList);
const contextQuestionList = computed(
  () => store.state.questions.contextQuestionList
);
const loading = computed(() => store.state.questions.loading);
const contextQuestionLoading = computed(
  () => store.state.questions.contextQuestionLoading
);
const hasContext = computed(
  () =>
    store.state.context.context.trim().length > 5 ||
    store.state.context.documents.length > 0 ||
    store.state.context.images.length > 0
);

const chosen = computed(() => store.getters["questions/chosenOptionsById"]);
const allQuestions = computed(() => [
  ...contextQuestionList.value,
  ...questionList.value,
]);
const total = computed(() => allQuestions.value.length);
const answered = computed(
  () =>
    allQuestions.value.filter((question) => chosen.value[question.id]?.length)
      .length
);

// Placeholders for the questions still on their way
const coming = computed(() =>
  loading.value ? Math.max(1, QUESTION_COUNT - questionList.value.length) : 0
);
const failed = computed(
  () => !loading.value && questionList.value.length === 0
);

const restart = () =>
  store.dispatch("changePrompt", { prompt: prompt.value, force: true });
const askAboutContext = () =>
  store.dispatch("questions/addContextQuestions", { replaceQuestions: true });
const openContext = () => {
  const tab = store.state.tabs.tabList.find((tab) => tab.name === "Context");
  if (tab) store.dispatch("tabs/setTab", tab);
};
</script>

<template>
  <div class="space-y-4">
    <header>
      <div class="flex items-center justify-between gap-4 sm:items-end">
        <div>
          <h2 class="text-base font-bold text-primary sm:text-lg">
            Help the genie get it right
          </h2>
          <p class="hidden text-sm text-secondary sm:block">
            Pick any answers that fit, or add your own. Skip anything that
            doesn't matter.
          </p>
        </div>
        <p
          v-if="total"
          class="shrink-0 whitespace-nowrap text-sm font-bold text-primary"
          role="status"
        >
          {{ answered }} of {{ total }} answered
        </p>
      </div>
      <div
        v-if="total"
        class="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-10"
        aria-hidden="true"
      >
        <div
          class="h-full rounded-full bg-accentSecondary transition-all duration-500"
          :style="{ width: `${(answered / total) * 100}%` }"
        />
      </div>
    </header>

    <div
      v-if="failed"
      class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-primary"
      role="alert"
    >
      <p>The questions didn't arrive this time.</p>
      <button
        type="button"
        class="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-secondary"
        @click="restart"
      >
        Try again
      </button>
    </div>

    <section
      v-if="hasContext"
      class="space-y-2.5 sm:space-y-3"
      aria-label="Questions about what you added"
    >
      <p class="text-xs font-bold uppercase tracking-wide text-secondary">
        About what you added
      </p>
      <QuestionCard
        v-for="que in contextQuestionList"
        :key="que.id"
        :question="que"
      />
      <p
        v-if="contextQuestionLoading"
        class="flex animate-pulse items-center rounded-2xl border border-gray-200 p-4 text-sm text-secondary"
      >
        <font-awesome-icon
          :icon="['fas', 'wand-magic-sparkles']"
          class="mr-2 text-accentPrimary"
        />
        Reading what you added...
      </p>
      <button
        v-else-if="!contextQuestionList.length"
        type="button"
        class="flex w-full items-center justify-center rounded-2xl border border-dashed border-secondaryLight p-4 text-sm font-bold text-primary transition-colors hover:border-accentPrimary hover:text-accentPrimary"
        @click="askAboutContext"
      >
        <font-awesome-icon :icon="['fas', 'list-check']" class="mr-2" />
        Ask me about what I added
      </button>
    </section>

    <section
      id="shepherd-target-questions"
      class="space-y-2.5 sm:space-y-3"
      aria-label="Questions"
      :aria-busy="loading"
    >
      <p
        v-if="hasContext"
        class="text-xs font-bold uppercase tracking-wide text-secondary"
      >
        About your make
      </p>
      <p
        v-if="loading && !questionList.length"
        class="flex items-center text-sm text-secondary"
      >
        <font-awesome-icon
          :icon="['fas', 'wand-magic-sparkles']"
          class="mr-2 animate-pulse text-accentPrimary"
        />
        Thinking up questions about your make...
      </p>
      <QuestionCard
        v-for="(que, index) in questionList"
        :id="index === 0 ? 'shepherd-target-question-item' : undefined"
        :key="que.id"
        :question="que"
        :number="index + 1"
        swappable
      />
      <div
        v-for="n in coming"
        :key="'coming' + n"
        class="animate-pulse rounded-2xl border border-gray-200 p-4 sm:p-5"
        aria-hidden="true"
      >
        <div class="flex items-center gap-3">
          <span class="h-7 w-7 rounded-full bg-gray-10" />
          <span class="h-4 w-2/3 rounded bg-gray-10" />
        </div>
        <div class="mt-4 flex gap-2 sm:pl-10">
          <span class="h-8 w-24 rounded-2xl bg-gray-10" />
          <span class="h-8 w-32 rounded-2xl bg-gray-10" />
          <span class="h-8 w-20 rounded-2xl bg-gray-10" />
        </div>
      </div>
    </section>

    <p
      v-if="!hasContext && questionList.length && !loading"
      class="text-sm text-secondary"
    >
      Got something the genie should read, like a job advert or an email to
      reply to?
      <button
        type="button"
        class="font-bold text-accentPrimary hover:text-purple-400"
        @click="openContext"
      >
        Add it on the Context tab
      </button>
    </p>
  </div>
</template>
